import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ClipboardList, Container, CreditCard, MapPinned, RefreshCcw, ShieldAlert, Truck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DataErrorBanner, type DataErrorDetail } from '@/components/ui/data-error-banner';
import { formatDateTime, formatMoney, useDepotDashboard } from './depot-hooks';
import {
  DepotEmptyRow,
  DepotGridPanel,
  depotTableClassName,
  depotTableHeadClassName,
  depotTdClassName,
  depotThClassName,
} from './depot-grid';

const KPI_CONFIG = [
  { key: 'gateTransactionsToday', label: 'Gate Today', icon: Truck, color: 'text-sky-700', route: '/depot/gate' },
  { key: 'inYardContainers', label: 'In Yard', icon: Container, color: 'text-emerald-700', route: '/depot/containers' },
  { key: 'pendingInspections', label: 'Pending Inspection', icon: ClipboardList, color: 'text-amber-700', route: '/depot/inspections' },
  { key: 'pendingMoves', label: 'Pending Moves', icon: MapPinned, color: 'text-violet-700', route: '/depot/moves' },
  { key: 'unbilledCharges', label: 'Unbilled Charges', icon: CreditCard, color: 'text-rose-700', route: '/depot/billing' },
  { key: 'pendingApprovals', label: 'Pending Approvals', icon: ShieldAlert, color: 'text-slate-700', route: '/approvals?status=pending' },
] as const;

export default function DepotDashboard() {
  const { t } = useTranslation();
  const dashboard = useDepotDashboard();
  const [refreshing, setRefreshing] = useState(false);

  const dashError: DataErrorDetail | null = dashboard.error
    ? {
        endpoint: '/depot/dashboard/kpi',
        message: dashboard.error,
        status: 500,
        code: 'DEPOT_DASHBOARD_LOAD_FAILED',
      }
    : null;

  const handleRefresh = async () => {
    setRefreshing(true);
    await dashboard.refresh();
    setRefreshing(false);
  };

  const kpiValues = useMemo(() => {
    const src = dashboard.kpi;
    return KPI_CONFIG.map((item) => ({
      ...item,
      value: src ? src[item.key] : 0,
    }));
  }, [dashboard.kpi]);

  return (
    <div className="space-y-5">
      {dashError && <DataErrorBanner error={dashError} entityLabel={t('depotModule.dashboardTitle')} onRetry={handleRefresh} />}

      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">{t('depotModule.dashboardTitle')}</h2>
          <p className="text-sm text-muted-foreground">
            {t('depotModule.dashboardSubtitle')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" asChild>
            <Link to="/depot/accounts">Accounts</Link>
          </Button>
          <Button variant="outline" size="sm" asChild>
            <Link to="/depot/revenue">Revenue</Link>
          </Button>
          <Button variant="outline" size="sm" asChild>
            <Link to="/depot/profitability">Profitability</Link>
          </Button>
          <Button variant="outline" size="sm" asChild>
            <Link to="/depot/tariff">Tariff</Link>
          </Button>
          <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing || dashboard.loading}>
            <RefreshCcw className={`mr-1.5 h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
            {t('commonAction.refresh')}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
        {kpiValues.map((item) => {
          const Icon = item.icon;
          return (
            <Link key={item.key} to={item.route} className="rounded-lg border bg-background px-3 py-3 hover:bg-muted/30">
              <div className="mb-1 flex items-center justify-between">
                <span className="text-xs font-medium text-muted-foreground">{item.label}</span>
                <Icon className={`h-4 w-4 ${item.color}`} />
              </div>
              <div className="text-2xl font-bold">{dashboard.loading ? '—' : item.value}</div>
              <div className="text-[10px] text-muted-foreground">Open workbench</div>
            </Link>
          );
        })}
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <DepotGridPanel
          title="Recent Gate"
          subtitle="Latest truck/container gate transactions."
          action={<Button variant="ghost" size="sm" asChild><Link to="/depot/gate">Open Gate</Link></Button>}
        >
          <table className={depotTableClassName}>
            <thead className={depotTableHeadClassName}>
              <tr>
                <th className={depotThClassName}>Txn</th>
                <th className={depotThClassName}>Type</th>
                <th className={depotThClassName}>Container</th>
                <th className={depotThClassName}>Truck</th>
                <th className={depotThClassName}>Performed</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {dashboard.recentGate.length === 0 ? (
                <DepotEmptyRow colSpan={5}>No gate transactions yet.</DepotEmptyRow>
              ) : (
                dashboard.recentGate.map((item) => (
                  <tr key={item.id} className="hover:bg-muted/30">
                    <td className={`${depotTdClassName} font-mono font-semibold`}>{item.gate_txn_no}</td>
                    <td className={depotTdClassName}><Badge variant="outline">{item.gate_type}</Badge></td>
                    <td className={depotTdClassName}>{item.container_no}</td>
                    <td className={depotTdClassName}>{item.truck_plate ?? 'Truck TBC'}</td>
                    <td className={`${depotTdClassName} text-xs text-muted-foreground`}>{formatDateTime(item.performed_at)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </DepotGridPanel>

        <DepotGridPanel
          title="Recent Containers"
          subtitle="Active visits by condition, hold and yard position."
          action={<Button variant="ghost" size="sm" asChild><Link to="/depot/containers">Open Containers</Link></Button>}
        >
          <table className={depotTableClassName}>
            <thead className={depotTableHeadClassName}>
              <tr>
                <th className={depotThClassName}>Container</th>
                <th className={depotThClassName}>Depot / Slot</th>
                <th className={depotThClassName}>Status</th>
                <th className={depotThClassName}>Condition / Hold</th>
                <th className={depotThClassName}>Last Event</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {dashboard.recentContainers.length === 0 ? (
                <DepotEmptyRow colSpan={5}>No active visits yet.</DepotEmptyRow>
              ) : (
                dashboard.recentContainers.map((item) => (
                  <tr key={item.id} className="hover:bg-muted/30">
                    <td className={`${depotTdClassName} font-mono font-semibold`}>{item.container_no}</td>
                    <td className={depotTdClassName}>{item.depot_code ?? '—'} · {item.current_slot_code ?? 'No slot'}</td>
                    <td className={depotTdClassName}><Badge variant="outline">{item.visit_status}</Badge></td>
                    <td className={depotTdClassName}>{item.condition_status} · {item.hold_status}</td>
                    <td className={`${depotTdClassName} text-xs text-muted-foreground`}>{formatDateTime(item.last_event_at)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </DepotGridPanel>

        <DepotGridPanel
          title="Pending Moves"
          subtitle="Yard move work queue."
          action={<Button variant="ghost" size="sm" asChild><Link to="/depot/moves">Open Moves</Link></Button>}
        >
          <table className={depotTableClassName}>
            <thead className={depotTableHeadClassName}>
              <tr>
                <th className={depotThClassName}>Move</th>
                <th className={depotThClassName}>Container</th>
                <th className={depotThClassName}>From</th>
                <th className={depotThClassName}>To</th>
                <th className={depotThClassName}>Type / Status</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {dashboard.pendingMoves.length === 0 ? (
                <DepotEmptyRow colSpan={5}>No pending moves.</DepotEmptyRow>
              ) : (
                dashboard.pendingMoves.map((item) => (
                  <tr key={item.id} className="hover:bg-muted/30">
                    <td className={`${depotTdClassName} font-mono font-semibold`}>{item.move_no}</td>
                    <td className={depotTdClassName}>{item.container_no}</td>
                    <td className={depotTdClassName}>{item.from_slot_code ?? '—'}</td>
                    <td className={depotTdClassName}>{item.to_slot_code ?? '—'}</td>
                    <td className={depotTdClassName}>{item.move_type} · <Badge variant="outline">{item.task_status}</Badge></td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </DepotGridPanel>

        <DepotGridPanel
          title="Unbilled Charges"
          subtitle="Costing and billing exceptions needing finance handoff."
          action={<Button variant="ghost" size="sm" asChild><Link to="/depot/billing">Open Billing</Link></Button>}
        >
          <table className={depotTableClassName}>
            <thead className={depotTableHeadClassName}>
              <tr>
                <th className={depotThClassName}>Charge</th>
                <th className={depotThClassName}>Container</th>
                <th className={depotThClassName}>Type</th>
                <th className={depotThClassName}>Amount</th>
                <th className={depotThClassName}>Status</th>
                <th className={depotThClassName}>Event</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {dashboard.unbilledCharges.length === 0 ? (
                <DepotEmptyRow colSpan={6}>No unbilled charges.</DepotEmptyRow>
              ) : (
                dashboard.unbilledCharges.map((item) => (
                  <tr key={item.id} className="hover:bg-muted/30">
                    <td className={`${depotTdClassName} font-mono font-semibold`}>{item.charge_no}</td>
                    <td className={depotTdClassName}>{item.container_no}</td>
                    <td className={depotTdClassName}>{item.charge_type}</td>
                    <td className={depotTdClassName}>{formatMoney(item.amount, item.currency)}</td>
                    <td className={depotTdClassName}>
                      <Badge variant={item.charge_status === 'disputed' ? 'destructive' : 'outline'}>{item.charge_status}</Badge>
                    </td>
                    <td className={`${depotTdClassName} text-xs text-muted-foreground`}>{formatDateTime(item.event_at)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </DepotGridPanel>

        <div className="xl:col-span-2">
          <DepotGridPanel
            title="Pending Approvals"
            subtitle="All urgent depot approvals in one queue, not split into multiple menus."
            action={<Button variant="ghost" size="sm" asChild><Link to="/approvals?status=pending">Open Approvals</Link></Button>}
          >
            <table className={depotTableClassName}>
              <thead className={depotTableHeadClassName}>
                <tr>
                  <th className={depotThClassName}>Approval</th>
                  <th className={depotThClassName}>Type</th>
                  <th className={depotThClassName}>Reviewer</th>
                  <th className={depotThClassName}>Priority</th>
                  <th className={depotThClassName}>Status</th>
                  <th className={depotThClassName}>Created</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {dashboard.pendingApprovals.length === 0 ? (
                  <DepotEmptyRow colSpan={6}>No pending depot approvals.</DepotEmptyRow>
                ) : (
                  dashboard.pendingApprovals.map((item) => (
                    <tr key={item.id} className="hover:bg-muted/30">
                      <td className={depotTdClassName}>{item.title}</td>
                      <td className={depotTdClassName}>{item.request_type}</td>
                      <td className={depotTdClassName}>{item.reviewer_role ?? '—'}</td>
                      <td className={depotTdClassName}><Badge variant="outline">{item.priority}</Badge></td>
                      <td className={depotTdClassName}>{item.status}</td>
                      <td className={`${depotTdClassName} text-xs text-muted-foreground`}>{formatDateTime(item.created_at)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </DepotGridPanel>
        </div>
      </div>
    </div>
  );
}
